/**
 * One coffee machine: its state, its alarms, when it last reported, who last
 * woke it or put it to sleep, the grounds and descale meters, Wake or Put to
 * sleep when either makes sense, and Statistics. The page shows one per
 * machine; the dashboard shows one at a time, with arrows in `controls`.
 *
 * - **It reads its own status**, every minute while the tab is visible, and
 *   every 15 s while the answer is `unknown` or `waking`, or for three minutes
 *   after a wake while it still says standby. The server is fetching a fresh
 *   report then; the cloud is protected by the server's cache, not by this card.
 * - **A failed read shows in the card**, not on the screen: one machine whose
 *   cloud is down costs only its own card.
 * - **Refresh** asks the server for a fresh read: it skips the server's cache
 *   and asks the machine for a new report, so it takes a few seconds. The
 *   server limits how often that reaches the cloud.
 * - **The picture** is the model's, loaded from the manufacturer's server.
 *   None, or one that fails to load, leaves the card as it is without it.
 * - **Put to sleep asks first**: somebody at the machine may be about to use
 *   it. The server offers it only while the machine is ready.
 * - **Who last woke it** shows for 12 hours: after that it is not news.
 * - **Counters are read once**, when the card mounts and on Refresh — not
 *   polled. When the server asked the machine for new ones (they were old, or
 *   Refresh), the card reads them again once, 40 s later. The meters and
 *   Statistics share the read. A failure shows only behind Statistics; the
 *   meters are left out.
 * - Remount it with a `key` for another machine; the poll belongs to one.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import CoffeeIcon from '@mui/icons-material/Coffee';
import BarChartIcon from '@mui/icons-material/BarChart';
import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip, { type ChipProps } from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { CoffeeMachineDto, CoffeeMachineState, CoffeeMachineStatusDto } from '@forsys/shared';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorState } from '../../components/ErrorState';
import { Panel } from '../../components/Panel';
import { Timestamp } from '../../components/Timestamp';
import { usePolledValue } from '../../hooks/usePolledValue';
import { useSubmit } from '../../hooks/useSubmit';
import { useAsync } from '../../hooks/useAsync';
import { getMachineCounters, getMachineStatus, standbyMachine, wakeMachine } from './api';
import { CountersDetails, MaintenanceMeters } from './MachineCounters';

const SLOW_MS = 60_000;
const FAST_MS = 15_000;
/** After a wake, poll fast while the machine still says standby — for this long at most. */
const WOKEN_WINDOW_MS = 3 * 60_000;
/** Who last woke the machine is shown for this long. */
const LAST_POWER_MS = 12 * 3600_000;
/** After the server asked the machine for new counters: when to read them again. The machine answered in 28 s. */
const COUNTERS_AGAIN_MS = 40_000;

const STATE_COLOR: Readonly<Record<CoffeeMachineState, ChipProps['color']>> = {
  ready: 'success',
  busy: 'info',
  waking: 'info',
  standby: 'default',
  offline: 'error',
  unknown: 'warning',
};

/** A read that never rejects, so `usePolledValue` keeps the failure instead of dropping it. */
type Read = { status: CoffeeMachineStatusDto; error?: undefined } | { status?: undefined; error: Error };

export interface MachineCardProps {
  machine: CoffeeMachineDto;
  /** At the right of the title row: the dashboard's arrows. */
  controls?: ReactNode;
}

export function MachineCard({ machine, controls }: MachineCardProps) {
  const { t } = useTranslation('coffee');
  const [wokenAt, setWokenAt] = useState<number | null>(null);
  const [fast, setFast] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [confirmingStandby, setConfirmingStandby] = useState(false);
  const [slept, setSlept] = useState(false);
  const [countersOpen, setCountersOpen] = useState(false);

  const read = async (fresh = false): Promise<Read> => {
    let next: Read;
    try {
      next = { status: (await getMachineStatus(machine.id, fresh)).status };
    } catch (error) {
      next = { error: error instanceof Error ? error : new Error(String(error)) };
    }
    const state = next.status?.state;
    const justWoken = wokenAt !== null && Date.now() - wokenAt < WOKEN_WINDOW_MS && state === 'standby';
    setFast(state === 'unknown' || state === 'waking' || justWoken);
    return next;
  };
  const [current, setCurrent] = usePolledValue<Read | null>(read, null, fast ? FAST_MS : SLOW_MS);
  const counters = useAsync(() => getMachineCounters(machine.id), [machine.id]);
  const countersUpdating = counters.data?.counters.updateRequested === true;
  const { reload: reloadCounters, setData: setCounters } = counters;
  useEffect(() => {
    if (!countersUpdating) return;
    const timer = window.setTimeout(reloadCounters, COUNTERS_AGAIN_MS);
    return () => window.clearTimeout(timer);
  }, [countersUpdating, counters.data, reloadCounters]);

  const refresh = useSubmit(async () => {
    // Not awaited: the status answers first; the counters land when they do.
    void getMachineCounters(machine.id, true).then(setCounters, () => reloadCounters());
    const next = await read(true);
    setCurrent(next);
    return next;
  });

  const wake = useSubmit(async () => {
    const result = await wakeMachine(machine.id);
    setWokenAt(Date.now());
    setSlept(false);
    setFast(true);
    setCurrent(await read());
    return result;
  });

  const standby = useSubmit(async () => {
    const result = await standbyMachine(machine.id);
    setConfirmingStandby(false);
    setWokenAt(null);
    setSlept(true);
    setCurrent(await read());
    return result;
  });

  const status = current?.status;
  const lastPower =
    status?.lastPower && Date.now() - Date.parse(status.lastPower.at) < LAST_POWER_MS ? status.lastPower : null;
  const subtitle = [machine.model, machine.accountLabel].filter(Boolean).join(' · ');

  return (
    <Panel
      icon={<CoffeeIcon fontSize="small" />}
      title={machine.name}
      actions={
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Tooltip title={t('machine.refresh')}>
            <span>
              <IconButton
                size="small"
                aria-label={t('machine.refresh')}
                disabled={refresh.pending || wake.pending || standby.pending}
                onClick={() => void refresh.submit()}
              >
                {refresh.pending ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          {controls}
        </Stack>
      }
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        {machine.imageUrl && !imageFailed && (
          <Box
            component="img"
            src={machine.imageUrl}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setImageFailed(true)}
            sx={{ width: 72, height: 96, objectFit: 'contain', flexShrink: 0 }}
          />
        )}
        <Stack spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}

          {current === null && (
            <Stack spacing={0.5} aria-label={t('machine.loading')}>
              <Skeleton variant="rounded" width={110} height={24} />
              <Skeleton width="60%" />
            </Stack>
          )}

          {current?.error && <ErrorState error={current.error} severity="warning" />}

          {status && (
            <>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Tooltip title={status.detail ?? ''}>
                  <Chip size="small" color={STATE_COLOR[status.state]} label={t(`state.${status.state}`)} />
                </Tooltip>
                {status.alarms.map((alarm) => (
                  <Chip
                    key={alarm}
                    size="small"
                    variant="outlined"
                    color="warning"
                    icon={<WarningAmberIcon />}
                    label={t(`alarm.${alarm}`)}
                  />
                ))}
              </Stack>

              <Typography variant="body2" color="text.secondary">
                {status.reportedAt ? (
                  <>
                    {t('machine.reported')} <Timestamp value={status.reportedAt} />
                  </>
                ) : (
                  t('machine.noReport')
                )}
              </Typography>

              {lastPower && (
                <Typography variant="body2" color="text.secondary">
                  {t(lastPower.command === 'wake' ? 'machine.lastWake' : 'machine.lastStandby', { by: lastPower.by })}
                  {', '}
                  <Timestamp value={lastPower.at} />
                </Typography>
              )}

              {counters.data && <MaintenanceMeters counters={counters.data.counters} />}
              {countersUpdating && (
                <Typography variant="caption" color="text.secondary">
                  {t('counters.updating')}
                </Typography>
              )}
            </>
          )}

          {wokenAt !== null && !wake.pending && status?.state !== 'ready' && status?.state !== 'busy' && (
            <Alert severity="success">{t('machine.wakeSent')}</Alert>
          )}
          {slept && !standby.pending && status?.state !== 'standby' && <Alert severity="success">{t('machine.standbySent')}</Alert>}
          {wake.error && <ErrorState error={wake.error} severity="warning" />}

          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {(status?.canWake || wake.pending) && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PowerSettingsNewIcon />}
                disabled={wake.pending}
                onClick={() => void wake.submit()}
              >
                {wake.pending ? t('machine.waking') : t('machine.wake')}
              </Button>
            )}
            {status?.canStandby && !wake.pending && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<BedtimeOutlinedIcon />}
                disabled={standby.pending}
                onClick={() => setConfirmingStandby(true)}
              >
                {t('machine.standby')}
              </Button>
            )}
            <Button
              size="small"
              startIcon={<BarChartIcon />}
              aria-expanded={countersOpen}
              onClick={() => setCountersOpen((open) => !open)}
            >
              {countersOpen ? t('counters.hide') : t('counters.show')}
            </Button>
          </Stack>

          <Collapse in={countersOpen} unmountOnExit>
            {counters.error ? (
              <ErrorState error={counters.error} severity="warning" onRetry={counters.reload} />
            ) : counters.data ? (
              <CountersDetails counters={counters.data.counters} />
            ) : (
              <CircularProgress size={20} aria-label={t('counters.loading')} />
            )}
          </Collapse>
        </Stack>
      </Stack>
      <ConfirmDialog
        open={confirmingStandby}
        title={t('machine.standbyTitle', { name: machine.name })}
        message={t('machine.standbyMessage')}
        confirmLabel={t('machine.standby')}
        pending={standby.pending}
        error={standby.error}
        onConfirm={() => void standby.submit()}
        onClose={() => {
          standby.clearError();
          setConfirmingStandby(false);
        }}
      />
    </Panel>
  );
}
